
"use client";

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MOCK_CAR_MAKES, type CarMake, type CarModel } from '@/app/lib/mock-db';
import { Camera, Car, Check, Loader2, Lightbulb, Sparkles, Info, Link as LinkIcon, AlertCircle, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getContextualTips } from '@/app/lib/tips-data';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { useCountry } from '@/context/CountryContext';
import { fetchCarMakes } from '@/lib/car-makes';
import { fetchCarModels, fetchCarTypes, type CarTypeOption } from '@/lib/car-taxonomy';
import { getStoredAccessToken } from '@/lib/auth/session';
import { getCitiesForCountry } from '@/lib/country-cities';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

export default function SellClient() {
  const { toast } = useToast();
  const { selectedCountryCode } = useCountry();
  const [carMakes, setCarMakes] = useState<CarMake[]>(MOCK_CAR_MAKES);
  const [carTypes, setCarTypes] = useState<CarTypeOption[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [customCity, setCustomCity] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    car_type_id: '',
    make_id: '',
    model_id: '',
    year: '',
    price: '',
    city: '',
    description: '',
    image_url: ''
  });

  // State for inline advice
  const [localAdvice, setLocalAdvice] = useState<string | null>(null);
  const [generatingAdvice, setGeneratingAdvice] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchCarMakes(), fetchCarTypes()]).then(([makesRows, typesRows]) => {
      if (!alive) return;
      if (makesRows.length > 0) setCarMakes(makesRows);
      if (typesRows.length > 0) setCarTypes(typesRows);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!formData.make_id) {
      setCarModels([]);
      return;
    }
    let alive = true;
    fetchCarModels(formData.make_id).then((rows) => {
      if (alive) setCarModels(rows);
    });
    return () => { alive = false; };
  }, [formData.make_id]);

  const selectedMakeModels = carModels;
  const cityOptions = getCitiesForCountry(selectedCountryCode);

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImportLoading(true);
    try {
      const response = await fetch('/api/import-listing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: importUrl, country_code: selectedCountryCode })
      });

      const result = await response.json();

      if (result.success && result.data) {
        setFormData((prev) => ({
          ...prev,
          title: result.data.title || '',
          description: result.data.description || '',
          price: result.data.price ? String(result.data.price) : '',
          image_url: result.data.image || ''
        }));
        setIsLinkDialogOpen(false);
        toast({
          title: "تم الاستيراد بنجاح",
          description: "تم جلب بعض البيانات من الرابط. يرجى مراجعتها وتكملة النقص.",
        });
      } else {
        throw new Error(result.error || 'فشل الاستيراد');
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "تنبيه",
        description: error.message || "لا يمكن جلب البيانات تلقائياً. يرجى الإدخال يدوياً.",
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = getStoredAccessToken();
      if (!token) {
        throw new Error('يرجى تسجيل الدخول أولاً حتى تتمكن من نشر الإعلان.');
      }

      const selectedMake = carMakes.find((m) => m.id === formData.make_id);
      const selectedType = carTypes.find((t) => t.id === formData.car_type_id);
      const selectedModel = selectedMakeModels.find((m) => m.id === formData.model_id);
      if (!selectedModel) {
        throw new Error('يرجى اختيار موديل السيارة من القائمة الجاهزة.');
      }
      if (!formData.city.trim()) {
        throw new Error('يرجى اختيار أو إدخال مدينة السيارة.');
      }

      const payload: Record<string, unknown> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: Number(formData.price),
        make_id: formData.make_id,
        make: selectedMake?.name_ar || selectedMake?.name_en || '',
        model_id: selectedModel.id,
        model: selectedModel.name_ar || selectedModel.name_en || '',
        year: Number(formData.year),
        city: formData.city.trim(),
        country_code: selectedCountryCode || 'SA',
      };

      if (selectedType) {
        payload.car_type_id = selectedType.id;
        payload.car_type = selectedType.name_ar || selectedType.name_en;
      }

      const response = await fetch(`${API}/api/marketplace/listings/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => null);
      if (!response.ok || (result && result.status === 'error')) {
        throw new Error(
          result?.message ||
          result?.detail ||
          'تعذر نشر الإعلان حالياً. يرجى مراجعة البيانات ثم المحاولة مرة أخرى.'
        );
      }

      setLoading(false);
      setSubmitted(true);
      toast({
        title: "تم بنجاح",
        description: "تم نشر إعلانك في سوق السيارات بنجاح.",
      });
    } catch (error: any) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "تعذر النشر",
        description: error?.message || "حدث خطأ غير متوقع أثناء نشر الإعلان.",
      });
    }
  };

  const generateAdvice = () => {
    setGeneratingAdvice(true);
    setTimeout(() => {
      const tips = getContextualTips('sell', 1);
      setLocalAdvice(tips[0].text_ar);
      setGeneratingAdvice(false);
    }, 800);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
          <Check className="h-10 w-10" />
        </div>
        <h2 className="text-3xl font-bold mb-2">شكراً لك!</h2>
        <p className="text-muted-foreground mb-8">إعلانك قيد المراجعة وسوف يظهر للجميع قريباً.</p>
        <Button onClick={() => window.location.href = '/buy'} className="rounded-2xl h-14 px-8 text-lg">الذهاب للسوق</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Choice Header */}
      <div className="grid grid-cols-3 gap-2">
        <Button
          variant="outline"
          className="h-20 rounded-[2rem] border-dashed border-primary flex flex-col gap-1 bg-white hover:bg-blue-50"
          onClick={() => {
            setCustomCity(false);
            setFormData({ title: '', car_type_id: '', make_id: '', model_id: '', year: '', price: '', city: '', description: '', image_url: '' });
          }}
        >
          <Car className="h-5 w-5 text-primary" />
          <span className="text-[10px] font-bold">إعلان يدوي</span>
        </Button>

        <Button
          variant="outline"
          className="h-20 rounded-[2rem] border-dashed border-indigo-500 flex flex-col gap-1 bg-gradient-to-br from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100"
          onClick={() => window.location.href = '/sell/scan-my-car'}
        >
          <Sparkles className="h-5 w-5 text-indigo-600" />
          <span className="text-[10px] font-bold text-indigo-900">تسعير وفحص ذكي</span>
        </Button>

        <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="h-20 rounded-[2rem] border-dashed border-secondary flex flex-col gap-1 bg-white hover:bg-orange-50"
            >
              <LinkIcon className="h-5 w-5 text-secondary" />
              <span className="text-[10px] font-bold">استيراد من رابط</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-[2.5rem] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-headline font-bold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-secondary" /> استيراد إعلان من فيسبوك/إنستجرام
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex items-start gap-3">
                <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-[10px] text-slate-600 font-medium">الصق رابط الإعلان من Facebook Marketplace أو Instagram وسنقوم بجلب البيانات لك تلقائياً.</p>
              </div>
              <Input
                placeholder="https://facebook.com/marketplace/item/..."
                className="rounded-2xl h-14 bg-slate-50 border-none"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                onClick={handleImport}
                disabled={importLoading || !importUrl.trim()}
                className="w-full h-14 rounded-2xl bg-secondary hover:bg-secondary/90 font-bold"
              >
                {importLoading ? <Loader2 className="animate-spin h-5 w-5" /> : "ابدأ الاستيراد"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-none shadow-xl rounded-3xl overflow-hidden">
        <CardHeader className="bg-slate-900 text-white pb-8">
          <div className="flex items-center gap-2 mb-2">
            <Car className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl font-headline font-black">بيانات الإعلان</CardTitle>
          </div>
          <p className="text-white/60 text-xs">يرجى التأكد من دقة البيانات لضمان وصول المشترين لك.</p>
        </CardHeader>
        <CardContent className="pt-8 px-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">عنوان الإعلان</label>
              <Input
                placeholder="مثلاً: تويوتا كامري 2020 فل كامل"
                className="h-14 rounded-2xl bg-slate-50 border-slate-200"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">ماركة السيارة</label>
              <Select
                required
                onValueChange={(val) => setFormData({ ...formData, make_id: val, model_id: '' })}
                value={formData.make_id}
              >
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200">
                  <SelectValue placeholder="اختر الماركة" />
                </SelectTrigger>
                <SelectContent>
                  {carMakes.map(make => (
                    <SelectItem key={make.id} value={make.id}>{make.name_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">نوع السيارة (اختياري)</label>
              <Select
                onValueChange={(val) => setFormData({ ...formData, car_type_id: val })}
                value={formData.car_type_id}
              >
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200">
                  <SelectValue placeholder="اختر نوع السيارة (سيدان، SUV...)" />
                </SelectTrigger>
                <SelectContent>
                  {carTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.name_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-bold mr-1">موديل السيارة</label>
              <Select
                onValueChange={(val) => setFormData({ ...formData, model_id: val })}
                value={formData.model_id}
                disabled={!formData.make_id}
              >
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200">
                  <SelectValue placeholder={formData.make_id ? "اختر من الموديلات المتاحة" : "اختر الماركة أولاً"} />
                </SelectTrigger>
                <SelectContent>
                  {selectedMakeModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>{model.name_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.make_id && selectedMakeModels.length === 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-[11px] font-bold text-amber-800">
                  لا توجد موديلات مفعّلة لهذه الماركة حالياً. يرجى مراجعة إدارة الموديلات من لوحة النظام.
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">سنة الصنع</label>
                <Input
                  type="number"
                  placeholder="2023"
                  className="h-14 rounded-2xl bg-slate-50 border-slate-200"
                  required
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">السعر المطلوب</label>
                <Input
                  type="number"
                  placeholder="مثلاً 85000"
                  className="h-14 rounded-2xl bg-slate-50 border-slate-200"
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">مدينة السيارة</label>
              <Select
                value={customCity ? '__other' : formData.city}
                onValueChange={(val) => {
                  if (val === '__other') {
                    setCustomCity(true);
                    setFormData({ ...formData, city: '' });
                  } else {
                    setCustomCity(false);
                    setFormData({ ...formData, city: val });
                  }
                }}
              >
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200">
                  <SelectValue placeholder="اختر المدينة حسب الدولة" />
                </SelectTrigger>
                <SelectContent>
                  {cityOptions.map((city) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                  <SelectItem value="__other">مدينة أخرى</SelectItem>
                </SelectContent>
              </Select>
              {customCity && (
                <Input
                  placeholder="اكتب اسم المدينة"
                  className="h-14 rounded-2xl bg-slate-50 border-slate-200"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              )}
              <p className="text-[11px] text-slate-400 font-bold">
                تظهر المدن حسب الدولة المختارة من إعدادات الموقع.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">وصف السيارة</label>
              <Textarea
                placeholder="اذكر حالة السيارة، الممشى، والمواصفات..."
                className="min-h-[120px] rounded-2xl bg-slate-50 border-slate-200 text-sm"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            {formData.image_url ? (
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">صورة الإعلان المستوردة</label>
                <div className="relative aspect-video w-full rounded-3xl overflow-hidden border-2 border-primary/20">
                  <img src={formData.image_url} alt="Imported" className="w-full h-full object-cover" />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 left-2 rounded-full h-8 w-8"
                    onClick={() => setFormData({ ...formData, image_url: '' })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-200 rounded-3xl p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer group">
                <div className="bg-white p-4 rounded-2xl inline-block shadow-sm group-hover:scale-110 transition-transform">
                  <Camera className="h-8 w-8 text-secondary" />
                </div>
                <p className="mt-4 font-bold text-slate-600">أضف صور السيارة</p>
                <p className="text-[10px] text-slate-400">الصور الواضحة تساعد على البيع بشكل أسرع</p>
              </div>
            )}

            <div className="space-y-3">
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 rounded-2xl border-dashed border-primary text-primary flex gap-2 font-bold hover:bg-blue-50 bg-white"
                  onClick={generateAdvice}
                  disabled={generatingAdvice}
                >
                  {generatingAdvice ? <Loader2 className="animate-spin h-4 w-4" /> : <Lightbulb className="h-4 w-4" />}
                  بماذا تنصحني؟
                </Button>

                {localAdvice && (
                  <div className="p-5 bg-orange-50 border border-orange-100 rounded-[2rem] animate-in fade-in slide-in-from-top-2 duration-500">
                    <div className="flex items-center gap-2 mb-2 text-secondary">
                      <Sparkles className="h-4 w-4" />
                      <h4 className="text-xs font-black uppercase">نصيحة الخبير:</h4>
                    </div>
                    <p className="text-sm font-bold text-slate-700 leading-relaxed">{localAdvice}</p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-16 rounded-2xl text-xl font-black bg-primary hover:bg-primary/90 shadow-xl shadow-primary/20"
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin h-6 w-6" /> : "نشر الإعلان الآن"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 p-4 bg-amber-50 rounded-2xl border border-amber-100">
        <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
        <p className="text-[10px] text-amber-800 font-bold leading-tight">تنبيه: عند الاستيراد من رابط، قد لا يتم جلب كل البيانات بدقة. يرجى مراجعة كافة الحقول قبل النشر النهائي.</p>
      </div>
    </div>
  );
}
